import { useState, useEffect, useRef } from "react";
import { SimpleGrid, Select, Button, Group, Stack, Divider } from '@mantine/core';
import supabase from "../api/client";
import { createPlayerGroups, createTeamGroups } from "../api/helpers";

export const Filters = ({
  close,
  setQuery,
  filters,
  setFilters,
  allEvents,
  allTeams,
  allPlayers
}) => {

  const viewport = useRef(null);
  const shouldScroll = useRef(false);
  const scrollToTop = () => {
    viewport.current.scrollTo({ top: 0})
    shouldScroll.current = false
  };

  // Event state
  const [currentEvent, setCurrentEvent] = useState(filters.event)
  // Game state
  const [loading, setLoading] = useState(false)
  const [currentGame, setCurrentGame] = useState(filters.game)
  const [games, setGames] = useState([])

  // Team state
  const [currentTeam, setCurrentTeam] = useState(filters.team)
  const [teams, setTeams] = useState(allTeams)

  // Player state
  const [currentPlayer, setCurrentPlayer] = useState(filters.player)
  const [players, setPlayers] = useState(allPlayers)

  useEffect(() => {
    if (filters.event) {
      getGames(filters.event)
    }
    
    if (filters.game) {
      getTeamsFromGame(filters.game)
      getPlayersFromGame(filters.game)
    }
    
    if (filters.team) {
      getPlayersFromTeam(filters.team)
    }
  }, [])

  const clearAll = () => {
    setGames([])
    setTeams(allTeams)
    setPlayers(allPlayers)

    setCurrentEvent(null)
    setCurrentGame(null)
    setCurrentTeam(null)
    setCurrentPlayer(null)
  }

  const apply = () => {
    let query = supabase.from("Highlight").select("id, title, videoId")

    if (currentEvent) {
      query = query.eq("eventId", currentEvent.id)
    }

    if (currentGame) {
      query = query.eq("gameId", currentGame.id)
    }

    if (currentTeam) {
      query = query.eq("teamId", currentTeam.id)
    }

    if (currentPlayer) {
      query = query.eq("playerId", currentPlayer.id)
    }

    query.order("created_at", { ascending: false })

    setQuery(query)

    if (currentEvent == null &&
      currentGame == null &&
      currentTeam == null &&
      currentPlayer == null
    ) setQuery(null)

    setFilters({
      "event": currentEvent,
      "game": currentGame,
      "team": currentTeam,
      "player": currentPlayer
    })

    close()
  }

  const handleEventChange = (option) => {
    if (option == null) {
      clearAll()
      return
    }

    shouldScroll.current = true;

    setCurrentEvent(option)

    setCurrentGame(null)
    getGames(option)

    setCurrentTeam(null)
    setTeams(allTeams)

    setCurrentPlayer(null)
    setPlayers(allPlayers)
  }

  // Game logic
  async function getGames(event) {
    setLoading(true)
    const { data } = await supabase.from("Game").select().eq("eventId", event.value).order("date", { ascending: false })
    const games = data.map(game => ({ ...game, value: (game.id).toString(), label: game.name }))
    setGames(games);
    setLoading(false)
  }

  const handleGameChange = (option) => {
    setCurrentTeam(null)
    setCurrentPlayer(null)

    if (option === null) {
      setCurrentGame(null)
      setTeams(allTeams)
      setPlayers(allPlayers)
      return
    }

    setCurrentGame(option)
    getTeamsFromGame(option)
    getPlayersFromGame(option)
  }

  async function getTeamsFromGame(game) {
    let newTeams = []

    for (let group of allTeams) {
      for (let item of group.items) {
        if (item.id === game.teamOneId || item.id === game.teamTwoId) {
          newTeams.push(item)
        }
      }
    }

    newTeams = createTeamGroups(newTeams).filter((item) => item.items.length > 0)
    setTeams(newTeams)
  }

  async function getPlayersFromGame(game) {
    let newPlayers = []

    for (let group of allPlayers) {
      for (let item of group.items) {
        if (item.teamId === game.teamOneId || item.teamId === game.teamTwoId) {
          newPlayers.push(item)
        }
      }
    }

    newPlayers = createPlayerGroups(newPlayers).filter((item) => item.items.length > 0)
    setPlayers(newPlayers)
  }

  const handleTeamChange = (option) => {
    setCurrentPlayer(null)

    if (option === null) {
      setCurrentTeam(null)

      if (currentGame) {
        getTeamsFromGame(currentGame)
        getPlayersFromGame(currentGame)
      }
      else {
        setTeams(allTeams)
        setPlayers(allPlayers)
      }

      return
    }

    setCurrentTeam(option)
    getPlayersFromTeam(option)
  }

  // Player logic
  async function getPlayersFromTeam(team) {
    let data = null
    for (let item of allPlayers) {
      if (item.group === team.name) {
        data = item.items
      }
    }

    let teamPlayers = data.map(player => ({ ...player, value: (player.id).toString(), label: player.name }))

    setPlayers(teamPlayers)
  }

  const handlePlayerChange = (option) => {
    setCurrentPlayer(option)
  }

  return (
    <SimpleGrid
      cols={1}
    >
      <Stack
        gap={"xl"}
        styles={{
          root: {
            paddingBottom: "3%"
          }
        }}
      >
        {allEvents ?
          <Select
            placeholder="Select Event"
            data={allEvents}
            value={currentEvent ? currentEvent.value : null}
            onChange={(value, option) => handleEventChange(option)}
            searchable
            checkIconPosition={"right"}
            nothingFoundMessage="No event found"
            comboboxProps={{position: "bottom", middlewares: {flip: false, shift: false}}}
            spellCheck={false}
          /> : null}
        {games ?
          <Select
            onClick={shouldScroll.current ? scrollToTop : null}
            id="test"
            placeholder={filters.game && currentGame ? "Loading..." : "Select Game"}
            data={loading ? [{ value: "Loading", label: "Loading...", disabled: true }] : games}
            value={currentGame ? currentGame.value : null}
            onChange={(value, option) => handleGameChange(option)}
            searchable
            checkIconPosition={"right"}
            nothingFoundMessage={currentEvent ? "No games found" : "Select an event"}
            spellCheck={false}
            comboboxProps={{position: "bottom", middlewares: {flip: false, shift: false}}}
            scrollAreaProps={{
              viewportRef: viewport,
            }}
          /> : null}

        <Divider />
        {allTeams ?
          <Select
            placeholder="Select Team"
            data={teams}
            value={currentTeam ? currentTeam.value : null}
            onChange={(value, option) => handleTeamChange(option)}
            searchable
            checkIconPosition={"right"}
            nothingFoundMessage="No team found"
            spellCheck={false}
            comboboxProps={{position: "bottom", middlewares: {flip: false, shift: false}}}
            styles={{
              groupLabel: {
                fontSize: "100%"
              }
            }}
          /> : null}
        {players ?
          <Select
            placeholder="Select Player"
            data={players}
            value={currentPlayer ? currentPlayer.value : null}
            onChange={(value, option) => handlePlayerChange(option)}
            searchable
            checkIconPosition={"right"}
            nothingFoundMessage="No player found"
            spellCheck={false}
            comboboxProps={{position: "bottom", middlewares: {flip: false, shift: false}}}
            styles={{
              groupLabel: {
                fontSize: "100%"
              }
            }}
          /> : null}
        <Divider />
      </Stack>

      <Group justify="center" gap="xl">
        <Button onClick={clearAll} radius="lg" size="sm">Reset</Button>
        <Button onClick={apply} radius="lg" size="sm">Apply</Button>
      </Group>
    </SimpleGrid>
  )
} 